'use strict'

const RFIDEvents = require('../lib/RFIDEvents')
const EventEmitter = require('events')

class NikeTable extends EventEmitter {
  constructor(address, options) {
    super()

    this.events = new RFIDEvents(address, options)
    this.events.on('connect', (data) => this.emit('connect', data))
    this.events.on('disconnect', (data) => this.emit('disconnect', data))
    this.events.on('tag_on_antenna', this._onEvent.bind(this))
    this.events.on('tag_near_antenna', this._onEvent.bind(this))
    this.events.on('tag_off_antenna', this._onEvent.bind(this))

    this._rfidTags = new Map()
    this._currentTags = new Map()
    this._currentOrientation = undefined
    this._newTags = []
    this._whitelistedTags = options.whitelistedTags
    this._orientationChangeDelay = parseInt(options?.orientationChangeDelay) || 2000
  }

  connect() {
    this.events.connect()
  }

  disconnect() {
    this.events.disconnect()
  }

  getStatus() {
    return this.events.getStatus()
  }

  getTag(id) {
    if (this._getRFIDTags(id).length > 0) {
      const position = this._getTagPosition(id)
      const side = this._getTagSide(id)

      return {id, side, position}
    }

    return undefined
  }

  getTags() {
    const tags = []
    const tagsMap = new Map()
    const rfidTags = this._getRFIDTags()
    rfidTags.forEach(tag => tagsMap.set(tag.id, this.getTag(tag.id)))
    tagsMap.forEach(tag => tags.push(tag))

    return tags
  }

  getNewTags() {
    return this._newTags.slice()
  }

  getOrientation() {
    const allTags = this._getRFIDTags()

    if (this._currentOrientation) {
      const currentOrientationTags = allTags.filter(t => {
        return this._currentOrientation === t.side
          && ['on_antenna', 'near_antenna'].includes(t.position)
      })

      if (currentOrientationTags.length > 0) {
        return this._currentOrientation
      }
    }

    const orientationTags = allTags.filter(t => {
      return ['normal', 'reverse'].includes(t.side)
        && ['on_antenna', 'near_antenna'].includes(t.position)
    })
    let oldestTag = this._getHighestRssiTag(orientationTags)

    return oldestTag?.side
  }

  addWhitelistedTag(tagId) {
    this._whitelistedTags = this._whitelistedTags || []
    this._whitelistedTags.push(tagId)
    this._newTags = this._newTags.filter(t => t !== tagId)
  }

  removeWhitelistedTag(tagId) {
    if (this._whitelistedTags) {
      this._whitelistedTags = this._whitelistedTags.filter(id => id !== tagId)
    }
  }

  _onEvent(data) {
    if (this._isTagWhitelisted(data)) {
      this._setRFIDTag({...data, seen: Date.now()})

      const newTag = this.getTag(data.id)
      const currentTag = this._currentTags.get(data.id)

      if (currentTag?.side !== newTag?.side || currentTag?.position !== newTag?.position) {
        this._currentTags.set(data.id, newTag)
        this.emit(`tag_${newTag.position}`, newTag)
      }

      this._scheduleOrientationUpdate();
    } else {
      const newTag = {id: data.id}

      if (data.position !== 'off_antenna') {
        if (this._newTags.filter(t => t === newTag.id).length === 0) {
          this._newTags.push(newTag.id)
          this.emit('tag_new', newTag)
        }
      } else {
        this._newTags = this._newTags.filter(t => t !== newTag.id)
      }
    }
  }

  _scheduleOrientationUpdate() {
    setTimeout(() => {
      const newOrientation = this.getOrientation()

      if (this._currentOrientation !== newOrientation) {
        this._currentOrientation = newOrientation
        this.emit('orientation_change', newOrientation)
      }
    }, this._orientationChangeDelay)
  }

  _isTagWhitelisted(tag) {
    return !this._whitelistedTags || this._whitelistedTags.indexOf(tag.id) >= 0
  }

  _getTagPosition(id) {
    const allTags = this._getRFIDTags()
    const targetTags = allTags.filter(t => id === t.id)

    return targetTags.reduce((currentPosition, currentTag) => {
      switch (currentTag.position) {
        case 'on_antenna':
          const newPosition = ['left', 'right'].includes(currentTag.side) ? 'on_hotspot' : 'near_table'
          return getHigherPriorityPosition(currentPosition, newPosition)
        case 'near_antenna':
          return getHigherPriorityPosition(currentPosition, 'near_table')
        default:
          return getHigherPriorityPosition(currentPosition, 'off_table')
      }
    }, 'off_table')

    function getHigherPriorityPosition(currentPosition, newPosition) {
      let positionsPriority = {'on_hotspot': 1, 'near_table': 2, 'off_table': 3}
      return positionsPriority[currentPosition] < positionsPriority[newPosition] ?
        currentPosition : newPosition
    }
  }

  _getTagSide(id) {
    const allTags = this._getRFIDTags()
    const sideTags = allTags.filter(t => {
      return id === t.id
        && ['on_antenna'].includes(t.position)
        && ['left', 'right'].includes(t.side)
    })
    const oldestTag = this._getHighestRssiTag(sideTags)

    return oldestTag?.side || null
  }

  _getHighestRssiTag(tags) {
    return tags.reduce((finalTag, currantTag) => {
      const finalTagRSSI = finalTag?.RSSI || Number.MIN_SAFE_INTEGER
      const currentTagRSSI = currantTag?.RSSI || Number.MIN_SAFE_INTEGER

      return finalTagRSSI > currentTagRSSI ? finalTag : currantTag
    }, null)
  }

  _setRFIDTag(data) {
    const tagsMap = this._rfidTags.get(data.antenna) || new Map()
    tagsMap.set(data.id, data)
    this._rfidTags.set(data.antenna, tagsMap)
  }

  _getRFIDTags(id = undefined) {
    let result = []
    this._rfidTags.forEach(tags => {
      tags.forEach(tag => result.push(tag))
    })

    if (id) {
      result = result.filter(t => t.id === id)
    }

    return result
  }
}

module.exports = NikeTable
